import type { FC } from 'react';

import type { APMAsset } from '@cognite/apm-client';
import type { OptionType } from '@cognite/cogs.js-v11';
import { Select } from '@cognite/cogs-core-v11/components/select/select.tsx';
import type { Asset } from '@cognite/sdk';

import { useAppConfigContext } from '../../../../../providers/is-idm-provider/app-config-provider';
import { useRootAPMAssetList } from '../../../../asset/hooks/use-query/use-root-apm-asset-list';
import { useRootAssetList } from '../../../../asset/hooks/use-query/use-root-asset-list';
import { getTitle } from '../../../../search/search-result/utils';

interface RootLocationDropdownProps {
  hasAppConfigPermission: boolean;
  disableInputs?: boolean;
  onAssetChange: (asset: Asset | APMAsset) => void;
  externalId?: string;
}

export const RootLocationDropdown: FC<RootLocationDropdownProps> = ({
  hasAppConfigPermission,
  disableInputs,
  onAssetChange,
  externalId,
}): JSX.Element => {
  const { isIdm } = useAppConfigContext();
  const { data: classicAssets = [] } = useRootAssetList({
    enabled: !isIdm,
  });
  const { data: apmAssets = [] } = useRootAPMAssetList({
    enabled: isIdm,
  });

  const assets: (Asset | APMAsset)[] = !isIdm ? classicAssets : apmAssets;

  const selectOptions = assets.map((asset) => ({
    value: asset.externalId,
    label: getTitle(asset),
  }));

  const selectedAsset = selectOptions.find(
    (asset) => asset.value === externalId
  );

  const handleAssetChange = (newAsset: OptionType<string>) => {
    const newSelectedAsset = assets.find(
      (asset: Asset | APMAsset) => asset.externalId === newAsset.value
    );
    if (newSelectedAsset) onAssetChange(newSelectedAsset);
  };

  return (
    <Select
      options={selectOptions}
      value={selectedAsset}
      onChange={handleAssetChange}
      disabled={!hasAppConfigPermission || disableInputs}
    />
  );
};
